import React from "react";
import {
  Intent,
  Button,
  Spinner,
  InputGroup,
  Callout,
  Label,
  FormGroup
} from "@blueprintjs/core";
import classnames from "classnames";
import { useHistory } from "react-router-dom";

import { Toolbar, ToolbarGroup } from "../../../../molecules/Toolbar";
import SettingsTab from "../../SettingsTab";
import SettingsForm from "../../SettingsForm";
import FormSection from "../../../../atoms/FormSection/FormSection";
import styles from "../ShippingSettings.module.scss";
import FormInfo from "../../../../atoms/FormInfo/FormInfo";
import SettingsBreadcrumb from "../../SettingsBreadcrumb";
import { useChangeShippingProfile } from "./hooks";
import { AddProductButton } from "./AddProductButton";
import { DeleteShippingProfileButton } from "./DeleteShippingProfileButton";
import FormHeading from "../../../../atoms/FormHeading/FormHeading";
import { getShippingRuleRange } from "./helpers";
import { CreateShippingZoneButton } from "./CreateShippingZoneButton";

function ShippingProfileDetail({
  loading,
  shippingProfile,
  selectedProductIds,
  profileId,
  selectedStates
}) {
  const history = useHistory();

  const editProfileProps = useChangeShippingProfile({
    shippingProfileId: profileId,
    selectedProductIds,
    name: shippingProfile.name
  });

  const {
    removeShippingProfileProduct,
    isSaving,
    productName,
    setProductName,
    saveEnabled,
    editShippingProfile
  } = editProfileProps;

  const BREADCRUMBS = [
    {
      text: "Shipping",
      onClick: () => history.push("/settings/shipping")
    },
    {
      text: !loading && shippingProfile && shippingProfile.name
    }
  ];

  if (loading || !shippingProfile) {
    return (
      <SettingsTab centered>
        <Spinner />
      </SettingsTab>
    );
  }

  return (
    <SettingsTab>
      <Toolbar title="Settings">
        <ToolbarGroup>
          <DeleteShippingProfileButton profileId={profileId} />
          <Button
            text="Save"
            intent={Intent.PRIMARY}
            disabled={!saveEnabled}
            loading={isSaving}
            onClick={editShippingProfile}
          />
        </ToolbarGroup>
      </Toolbar>

      <SettingsForm>
        <SettingsBreadcrumb items={BREADCRUMBS} />
        <FormInfo className={styles.shippingInfo}>
          Choose where you ship and how much you charge for shipping at
          checkout. To charge different rates for certain products, create a
          shipping profile.
        </FormInfo>
        <FormSection className={styles.formSectionInput}>
          <FormGroup>
            <Label>Name</Label>
            <InputGroup value={productName} onChange={setProductName} />
            <FormInfo className={styles.desc}>
              Customers won't see this.
            </FormInfo>
          </FormGroup>
          <FormHeading
            weight="normal"
            rightElement={
              <AddProductButton {...editProfileProps} profileId={profileId} />
            }
          >
            Products
          </FormHeading>
        </FormSection>
        {shippingProfile.products && !shippingProfile.products.length && (
          <Callout
            intent={Intent.NONE}
            icon="info-sign"
            className={styles.calloutSpace}
          >
            No products added - Add products to apply rates based on places you
            are to ship to.
          </Callout>
        )}
        {shippingProfile.products.map((product) => (
          <FormSection className={styles.formSection} key={product.id}>
            <div className={styles.productBox}>
              <div className={styles.thumb}>
                <img src={product.image} className={styles.imageStyle} />
              </div>
              <div className={styles.info}>
                {product.name}
                {product.parentId && (
                  <FormInfo className={styles.itemCount}>
                    {product.variantCount} of {product.totalVariants} variants
                  </FormInfo>
                )}
              </div>
              <div className={styles.options}>
                <Button
                  intent={Intent.PRIMARY}
                  minimal={true}
                  className={styles.buttonClose}
                  disabled={isSaving}
                  onClick={() => {
                    removeShippingProfileProduct(product.selectedIds);
                  }}
                >
                  &times;
                </Button>
              </div>
            </div>
          </FormSection>
        ))}
        <FormSection
          className={classnames(styles.formSection, styles.noBorder)}
        >
          <FormHeading
            weight="bold"
            rightElement={
              <CreateShippingZoneButton
                shippingProfileId={profileId}
                selectedStates={selectedStates}
              />
            }
          >
            Shipping Zones
          </FormHeading>
          {shippingProfile.shippingZones &&
            !shippingProfile.shippingZones.length && (
              <Callout
                intent={Intent.NONE}
                icon="info-sign"
                className={styles.calloutSpace}
              >
                No zones or rates - Add zones to create rates for places you
                want to ship to.
              </Callout>
            )}
          {shippingProfile.shippingZones.map((zone) => (
            <FormSection className={styles.shippingZone} key={zone.id}>
              <FormHeading
                icon="globe"
                weight="bold"
                rightElement={
                  <Button
                    minimal={true}
                    intent={Intent.PRIMARY}
                    onClick={() => {
                      history.push(
                        `/settings/shipping/${shippingProfile.id}/zone/${zone.id}`
                      );
                    }}
                  >
                    Manage
                  </Button>
                }
              >
                {zone.name}
              </FormHeading>
              {zone.shippingRules && !zone.shippingRules.length && (
                <span className={styles.danger}>No rates specified</span>
              )}
              {zone.shippingRules.map((rule) => (
                <FormSection className={styles.formSection}>
                  <FormInfo className={styles.title}>{rule.condition}</FormInfo>
                  <div className={styles.flexRow}>
                    <FormInfo className={styles.value}>
                      {getShippingRuleRange(rule.min, rule.max, rule.condition)}
                    </FormInfo>
                    <FormInfo className={styles.value}>
                      {rule.rate ? `$${rule.rate}` : `Free`}
                    </FormInfo>
                  </div>
                </FormSection>
              ))}
            </FormSection>
          ))}
        </FormSection>
      </SettingsForm>
    </SettingsTab>
  );
}

export default ShippingProfileDetail;
